import React, { useState, useEffect } from 'react';
import { 
  FiPlus, FiFilter, FiSearch, FiTrendingUp, 
  FiEye, FiDollarSign, FiUsers, FiBarChart2,
  FiEdit, FiTrash2, FiPlay, FiPause, FiCopy
} from 'react-icons/fi';
import SocialMediadashboardSidebar from '../SocialMediadashboardSidebar';
import { useAuth } from '../../../Context/AuthContext';

const Ads = () => {
  const [ads, setAds] = useState([]);
  const [filteredAds, setFilteredAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newAd, setNewAd] = useState({
    name: '',
    campaignId: '',
    platform: '',
    adType: 'image',
    headline: '',
    description: '',
    imageUrl: '',
    targetUrl: '',
    budget: '',
    bidStrategy: 'lowest_cost'
  });

  const { googleUserInfo } = useAuth();

  // Platform options
  const platforms = [
    { value: 'facebook', label: 'Facebook', color: 'text-blue-600' },
    { value: 'instagram', label: 'Instagram', color: 'text-pink-600' },
    { value: 'twitter', label: 'Twitter', color: 'text-blue-400' },
    { value: 'linkedin', label: 'LinkedIn', color: 'text-blue-700' }
  ];

  // Ad types
  const adTypes = [
    { value: 'image', label: 'Image Ad' },
    { value: 'video', label: 'Video Ad' },
    { value: 'carousel', label: 'Carousel' },
    { value: 'story', label: 'Story' }
  ];

  // Bid strategies
  const bidStrategies = [
    { value: 'lowest_cost', label: 'Lowest Cost' },
    { value: 'cost_cap', label: 'Cost Cap' },
    { value: 'bid_cap', label: 'Bid Cap' }
  ];

  // Load ads and campaigns from localStorage
  useEffect(() => {
    const loadData = () => {
      try {
        const storedAds = JSON.parse(localStorage.getItem('socialAds') || '[]');
        setAds(storedAds);
        setFilteredAds(storedAds);
      } catch (error) {
        console.error('Error loading ads:', error);
        setAds([]);
        setFilteredAds([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Get campaigns for filter
  const getCampaigns = () => {
    try {
      return JSON.parse(localStorage.getItem('socialCampaigns') || '[]');
    } catch (error) {
      return [];
    }
  };

  // Filter ads
  useEffect(() => {
    let filtered = ads;

    if (searchTerm) {
      filtered = filtered.filter(ad =>
        ad.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ad.headline.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(ad => ad.status === statusFilter);
    }

    if (platformFilter !== 'all') {
      filtered = filtered.filter(ad => ad.platform === platformFilter);
    }

    if (campaignFilter !== 'all') {
      filtered = filtered.filter(ad => ad.campaignId === campaignFilter);
    }

    setFilteredAds(filtered);
  }, [ads, searchTerm, statusFilter, platformFilter, campaignFilter]);

  // Create new ad
  const createAd = () => {
    if (!newAd.name || !newAd.platform || !newAd.budget) {
      alert('Please fill in all required fields');
      return;
    }

    const ad = {
      id: Date.now().toString(),
      ...newAd,
      status: 'draft',
      createdAt: new Date().toISOString(),
      metrics: {
        impressions: 0,
        clicks: 0,
        ctr: 0,
        cpc: 0,
        spend: 0,
        conversions: 0,
        frequency: 0
      }
    };

    const updatedAds = [...ads, ad];
    setAds(updatedAds);
    localStorage.setItem('socialAds', JSON.stringify(updatedAds));
    setShowCreateModal(false);
    setNewAd({
      name: '',
      campaignId: '',
      platform: '',
      adType: 'image',
      headline: '',
      description: '',
      imageUrl: '',
      targetUrl: '',
      budget: '',
      bidStrategy: 'lowest_cost'
    });
  };

  // Update ad status
  const updateAdStatus = (adId, status) => {
    const updatedAds = ads.map(ad =>
      ad.id === adId ? { ...ad, status } : ad
    );
    setAds(updatedAds);
    localStorage.setItem('socialAds', JSON.stringify(updatedAds));
  };

  // Duplicate ad
  const duplicateAd = (adId) => {
    const adToDuplicate = ads.find(ad => ad.id === adId);
    if (adToDuplicate) {
      const duplicatedAd = {
        ...adToDuplicate,
        id: Date.now().toString(),
        name: `${adToDuplicate.name} (Copy)`,
        status: 'draft',
        createdAt: new Date().toISOString()
      };

      const updatedAds = [...ads, duplicatedAd];
      setAds(updatedAds);
      localStorage.setItem('socialAds', JSON.stringify(updatedAds));
    }
  };

  // Delete ad
  const deleteAd = (adId) => {
    if (window.confirm('Are you sure you want to delete this ad?')) {
      const updatedAds = ads.filter(ad => ad.id !== adId);
      setAds(updatedAds);
      localStorage.setItem('socialAds', JSON.stringify(updatedAds));
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get platform info
  const getPlatformInfo = (platform) => {
    return platforms.find(p => p.value === platform) || { label: platform, color: 'text-gray-600' };
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50 items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FD5F20] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading ads...</p>
        </div>
      </div>
    );
  }

  const campaigns = getCampaigns();

  return (
    <div className="flex h-screen bg-gray-50">
      <SocialMediadashboardSidebar />
      
      <div className="flex-1 overflow-auto">
        {/* Header */}
        <header className="bg-white shadow-sm p-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Ads</h1>
              <p className="text-gray-600">Manage your social media advertisements</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center bg-[#FD5F20] text-white px-4 py-2 rounded-lg hover:bg-[#e55a1d]"
            >
              <FiPlus className="mr-2" />
              Create Ad
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4 mt-6">
            <div className="flex-1 min-w-[300px]">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search ads..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
                />
              </div>
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="completed">Completed</option>
            </select>

            <select
              value={platformFilter}
              onChange={(e) => setPlatformFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
            >
              <option value="all">All Platforms</option>
              {platforms.map(platform => (
                <option key={platform.value} value={platform.value}>
                  {platform.label}
                </option>
              ))}
            </select>

            <select
              value={campaignFilter}
              onChange={(e) => setCampaignFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
            >
              <option value="all">All Campaigns</option>
              {campaigns.map(campaign => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </option>
              ))}
            </select>
          </div>
        </header>

        {/* Ads Grid */}
        <main className="p-6">
          {filteredAds.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center">
              <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <FiTrendingUp className="text-gray-400 text-2xl" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No Ads Found</h3>
              <p className="text-gray-600 mb-6">
                {ads.length === 0 
                  ? 'Create your first ad to get started'
                  : 'No ads match your current filters'
                }
              </p>
              <button 
                onClick={() => setShowCreateModal(true)}
                className="bg-[#FD5F20] text-white px-4 py-2 rounded-lg hover:bg-[#e55a1d]"
              >
                Create Ad
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredAds.map(ad => {
                const platformInfo = getPlatformInfo(ad.platform);
                const campaign = campaigns.find(c => c.id === ad.campaignId);

                return (
                  <div key={ad.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    {/* Ad Preview */}
                    <div className="relative bg-gray-100 h-48">
                      {ad.imageUrl ? (
                        <img 
                          src={ad.imageUrl} 
                          alt={ad.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <FiEye className="text-4xl" />
                        </div>
                      )}
                      <div className="absolute top-3 right-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ad.status)}`}>
                          {ad.status}
                        </span>
                      </div>
                    </div>

                    {/* Ad Details */}
                    <div className="p-6">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold text-lg text-gray-900">{ad.name}</h3>
                        <span className={`text-xs font-medium ${platformInfo.color}`}>
                          {platformInfo.label}
                        </span>
                      </div>

                      {campaign && (
                        <p className="text-sm text-gray-600 mb-3">
                          Campaign: {campaign.name}
                        </p>
                      )}

                      <div className="mb-4">
                        <p className="font-medium text-sm text-gray-900">{ad.headline}</p>
                        <p className="text-sm text-gray-600 mt-1">{ad.description}</p>
                      </div>

                      {/* Metrics */}
                      <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                        <div>
                          <p className="text-gray-600">Impressions</p>
                          <p className="font-semibold">{ad.metrics.impressions.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">CTR</p>
                          <p className="font-semibold">{ad.metrics.ctr}%</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Spend</p>
                          <p className="font-semibold">${ad.metrics.spend}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">CPC</p>
                          <p className="font-semibold">${ad.metrics.cpc}</p>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                        <div className="flex space-x-2">
                          {ad.status === 'draft' && (
                            <button
                              onClick={() => updateAdStatus(ad.id, 'active')}
                              className="flex items-center text-green-600 hover:text-green-700 text-sm"
                            >
                              <FiPlay className="mr-1" />
                              Start
                            </button>
                          )}
                          {ad.status === 'active' && (
                            <button
                              onClick={() => updateAdStatus(ad.id, 'paused')}
                              className="flex items-center text-yellow-600 hover:text-yellow-700 text-sm"
                            >
                              <FiPause className="mr-1" />
                              Pause
                            </button>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => duplicateAd(ad.id)}
                            className="text-gray-400 hover:text-blue-600"
                            title="Duplicate"
                          >
                            <FiCopy />
                          </button>
                          <button className="text-gray-400 hover:text-gray-600">
                            <FiEdit />
                          </button>
                          <button 
                            onClick={() => deleteAd(ad.id)}
                            className="text-gray-400 hover:text-red-600"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>

      {/* Create Ad Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold">Create New Ad</h2>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ad Name *
                  </label>
                  <input
                    type="text"
                    value={newAd.name}
                    onChange={(e) => setNewAd({...newAd, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
                    placeholder="Enter ad name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Campaign
                  </label>
                  <select
                    value={newAd.campaignId}
                    onChange={(e) => setNewAd({...newAd, campaignId: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
                  >
                    <option value="">Select Campaign</option>
                    {campaigns.map(campaign => (
                      <option key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Platform *
                  </label>
                  <select
                    value={newAd.platform}
                    onChange={(e) => setNewAd({...newAd, platform: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
                  >
                    <option value="">Select Platform</option>
                    {platforms.map(platform => (
                      <option key={platform.value} value={platform.value}>
                        {platform.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ad Type
                  </label>
                  <select
                    value={newAd.adType}
                    onChange={(e) => setNewAd({...newAd, adType: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
                  >
                    {adTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Headline
                </label>
                <input
                  type="text"
                  value={newAd.headline}
                  onChange={(e) => setNewAd({...newAd, headline: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
                  placeholder="Enter headline"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={newAd.description}
                  onChange={(e) => setNewAd({...newAd, description: e.target.value})}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
                  placeholder="Enter ad description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Image URL
                </label>
                <input
                  type="url"
                  value={newAd.imageUrl}
                  onChange={(e) => setNewAd({...newAd, imageUrl: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target URL
                </label>
                <input
                  type="url"
                  value={newAd.targetUrl}
                  onChange={(e) => setNewAd({...newAd, targetUrl: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
                  placeholder="https://example.com"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Budget ($) *
                  </label>
                  <input
                    type="number"
                    value={newAd.budget}
                    onChange={(e) => setNewAd({...newAd, budget: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
                    placeholder="Enter budget"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bid Strategy
                  </label>
                  <select
                    value={newAd.bidStrategy}
                    onChange={(e) => setNewAd({...newAd, bidStrategy: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
                  >
                    {bidStrategies.map(strategy => (
                      <option key={strategy.value} value={strategy.value}>
                        {strategy.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={createAd}
                className="px-4 py-2 bg-[#FD5F20] text-white rounded-lg hover:bg-[#e55a1d]"
              >
                Create Ad
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Ads;